/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Custom Error implementation requires prototype manipulation */
// Source 1: https://github.com/adriengibrat/ts-custom-error
// Source 2: https://github.com/Jblew/typescript-chained-error

export class CustomError extends Error {
  name: string
  cause?: Error

  constructor({
    name,
    message,
    cause,
  }: {
    name?: string
    message?: string
    cause?: Error
  }) {
    super(message || cause?.message)
    this.cause = cause

    // set error name as constructor name, make it not enumerable to keep native Error behavior
    // see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/new.target#new.target_in_constructors
    // see https://github.com/adriengibrat/ts-custom-error/issues/30
    Object.defineProperty(this, 'name', {
      value: name || new.target.name,
      enumerable: true, // Sentry and nuxt require that Error name is enumerable. Is that a nuxt bug?
    })

    fixProto(this, new.target.prototype)
    fixStack(this)
    this.stack = appendToStack(this.stack, cause)
  }
}

/**
 * Fix the prototype chain of the error
 *
 * Use Object.setPrototypeOf
 * Support ES6 environments
 *
 * Fallback setting __proto__
 * Support IE11+, see https://docs.microsoft.com/en-us/scripting/javascript/reference/javascript-version-information
 */
function fixProto(target: Error, prototype: {}) {
  // fix the extended error prototype chain
  // because typescript __extends implementation can't
  // see https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work

  const setPrototypeOf: SetPrototypeOfFn = (Object as any).setPrototypeOf
  setPrototypeOf
    ? setPrototypeOf(target, prototype)
    : ((target as any).__proto__ = prototype)
}

/**
 * Capture and fix the error stack when available
 *
 * Use Error.captureStackTrace
 * Support v8 environments
 */
function fixStack(target: Error, fn = target.constructor) {
  // try to remove contructor from stack trace
  const captureStackTrace: CaptureStackTraceFn = (Error as any)
    .captureStackTrace
  captureStackTrace && captureStackTrace(target, fn)
}

function appendToStack(
  stack: string | undefined,
  cause: Error | undefined,
): string {
  let newStack = stack || ''
  if (cause) {
    newStack += '\n Caused by: ' + (cause.stack || cause)
  }
  return newStack
}

type SetPrototypeOfFn = (target: Error, prototype: {}) => void
type CaptureStackTraceFn = (target: Error, prototype: {}) => void
